import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import {
  correctorStateKeys,
  fleetEqualizationKeys,
  fleetReportKeys,
} from "../query-keys";
import type { BuildId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

export { correctorStateKeys } from "../query-keys";

type EventCorrectorStateUpdate = components["schemas"]["EventCorrectorStateUpdate"];

export function useCorrectorState(buildId: BuildId) {
  return useQuery({
    queryKey: correctorStateKeys.byBuild(buildId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/builds/{build_id}/corrector-state",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to fetch corrector state");
    },
    enabled: !!buildId,
  });
}

function invalidateCorrectorQueries(qc: ReturnType<typeof useQueryClient>, buildId: BuildId): void {
  qc.invalidateQueries({ queryKey: correctorStateKeys.byBuild(buildId) });
  // Fleet equalization/report recompute against current corrector state.
  qc.invalidateQueries({ queryKey: fleetEqualizationKeys.all });
  qc.invalidateQueries({ queryKey: fleetReportKeys.all });
}

export function useSetCorrectorSlot(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: EventCorrectorStateUpdate) => {
      const result = await api.PATCH(
        "/api/builds/{build_id}/corrector-state",
        { params: { path: { build_id: buildId } }, body: data },
      );
      return throwIfError(result, "Failed to update corrector state");
    },
    onSuccess: () => invalidateCorrectorQueries(qc, buildId),
  });
}

/** Clear every corrector slot for a build in one shot. */
export function useClearCorrectorState(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await api.DELETE(
        "/api/builds/{build_id}/corrector-state",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to clear corrector state");
    },
    onSuccess: () => invalidateCorrectorQueries(qc, buildId),
  });
}
